import { useMemo } from 'react';
import { z } from 'zod';
import { InfiniteScrollList } from '@/components/ui/infinite-scroll-list';
import type {
    InfiniteScrollListFieldPredicateDescriptor,
    InfiniteScrollListPhysicalJsonStructurePredicateDescriptor,
} from '@/components/ui/infinite-scroll-list.types';
import { StatusBadge } from '@/components/ui/status-badge';
import { useBreadcrumbNavigation } from '@/lib/breadcrumbs/useBreadcrumbNavigation';
import { paymentsApi } from '@/lib/pillarApi';
import type {
    MerchantTimeZone,
    MerchantPaymentRequestSearchRequest,
    PaymentRequestListItem,
    LosslessNumber,
} from '@pillar-portal/api-types';
import { formatTimestampForMerchantUi, PaymentRequestStatus } from '@pillar-portal/api-types';
import { formatPaymentRequestSourceDocumentReference } from './source-document-reference';
import { formatMonetaryAmount } from '@/lib/utils';
import { getTrimmedNameOrNull } from '@/lib/payments-infrastructure/displayLabels';

const PAYMENT_REQUEST_STATUS_OPTIONS: readonly {
    value: PaymentRequestStatus;
    label: string;
}[] = [
    { value: PaymentRequestStatus.DRAFT, label: 'Draft' },
    { value: PaymentRequestStatus.SENT, label: 'Sent' },
    { value: PaymentRequestStatus.VIEWED, label: 'Viewed' },
    { value: PaymentRequestStatus.PARTIALLY_PAID, label: 'Partially paid' },
    { value: PaymentRequestStatus.PAID, label: 'Paid' },
    { value: PaymentRequestStatus.EXPIRED, label: 'Expired' },
    { value: PaymentRequestStatus.CANCELED, label: 'Canceled' },
    { value: PaymentRequestStatus.REISSUED, label: 'Reissued' },
];

const PaymentRequestFieldPredicateDescriptors: readonly InfiniteScrollListFieldPredicateDescriptor[] =
    [
        {
            field: 'merchant_display_name',
            label: 'Merchant',
            operator: 'eq',
            valueType: 'string',
        },
        {
            field: 'source_document_reference',
            label: 'Source reference',
            operator: 'eq',
            valueType: 'string',
        },
        {
            field: 'title',
            label: 'Title',
            operator: 'eq',
            valueType: 'string',
        },
        {
            field: 'recipient_email',
            label: 'Recipient email',
            operator: 'eq',
            valueType: 'string',
        },
        {
            field: 'recipient_phone',
            label: 'Recipient phone',
            operator: 'eq',
            valueType: 'string',
        },
        {
            field: 'currency_code',
            label: 'Currency',
            operator: 'eq',
            valueType: 'string',
        },
        {
            field: 'status',
            label: 'Status',
            operator: 'in',
            valueType: 'string',
            options: PAYMENT_REQUEST_STATUS_OPTIONS,
        },
    ];

const PaymentRequestPhysicalJsonStructurePredicateDescriptors: readonly InfiniteScrollListPhysicalJsonStructurePredicateDescriptor[] =
    [
        { field: 'source_document_key', label: 'Source document key' },
        { field: 'payment_request_details', label: 'Request details' },
        { field: 'amount_breakdown', label: 'Amount breakdown' },
    ];

function formatAmount(amount: LosslessNumber | undefined, currencyCode: string): string {
    if (amount === undefined) {
        return '—';
    }
    return formatMonetaryAmount(amount, currencyCode);
}

function formatDate(value: string | undefined, timeZone: MerchantTimeZone | null): string {
    if (value === undefined) {
        return '—';
    }
    return formatTimestampForMerchantUi(value, timeZone);
}

export function MerchantPaymentRequestsListContent() {
    const breadcrumbNavigate = useBreadcrumbNavigation();
    const queryKeyPrefix: ReadonlyArray<string> = useMemo(() => ['merchantPaymentRequests'], []);

    return (
        <InfiniteScrollList<PaymentRequestListItem, number, ReadonlyArray<string>>
            pageTitle="Payment Requests"
            cardTitle="Payment Requests"
            queryPlaceholder="Search by reference, email, phone, or amount"
            queryKeyPrefix={queryKeyPrefix}
            pageSize={50}
            controlsPlacement="above-search"
            predicateControls={{
                fieldPredicates: PaymentRequestFieldPredicateDescriptors,
                jsonPathPredicates: PaymentRequestPhysicalJsonStructurePredicateDescriptors,
            }}
            fetchPage={async (params) => {
                const response = await paymentsApi.listMerchantPaymentRequests({
                    ...(params.terms !== undefined ? { terms: [...params.terms] } : {}),
                    field_predicates:
                        params.fieldPredicates === undefined
                            ? undefined
                            : [...params.fieldPredicates],
                    json_path_predicates:
                        params.jsonPathPredicates === undefined
                            ? undefined
                            : [...params.jsonPathPredicates],
                    limit: params.limit,
                    cursor: params.cursor,
                } satisfies MerchantPaymentRequestSearchRequest);
                return { items: response.payment_requests, pagination: response.pagination };
            }}
            parseCursorParam={(pageParam: unknown) => {
                if (pageParam === null || pageParam === undefined) {
                    return undefined;
                }
                const parseResult = z.number().safeParse(pageParam);
                if (!parseResult.success) {
                    throw new Error('Expected numeric cursor');
                }
                return parseResult.data;
            }}
            stateCopy={{
                loadingLabel: 'Loading payment requests...',
                emptyLabel: 'No payment requests found.',
                endOfListLabel: 'End of payment requests.',
                genericErrorLabel: 'Failed to load payment requests.',
            }}
            renderItems={({ items }) => (
                <div className="space-y-2">
                    {items.map((paymentRequest) => {
                        const formattedSourceDocumentReference =
                            formatPaymentRequestSourceDocumentReference({
                                sourceDocumentReference: paymentRequest.source_document_reference,
                                sourceDocumentKey: paymentRequest.source_document_key,
                            });

                        return (
                            <div
                                key={paymentRequest.payment_request_uuid}
                                className="w-full rounded-lg border p-4 text-left transition-colors hover:bg-gray-50"
                            >
                                <div className="flex items-start justify-between gap-6">
                                    <button
                                        type="button"
                                        className="min-w-0 flex-1 space-y-1 text-left"
                                        onClick={() => {
                                            breadcrumbNavigate({
                                                source: 'panel',
                                                target: {
                                                    url: `/payment-requests/${paymentRequest.payment_request_uuid}`,
                                                    title: 'Payment Request Details',
                                                },
                                            });
                                        }}
                                    >
                                        <div className="flex items-center gap-2">
                                            <h3 className="font-semibold">
                                                {formattedSourceDocumentReference ??
                                                    paymentRequest.title ??
                                                    'Payment Request'}
                                            </h3>
                                            <StatusBadge status={paymentRequest.status} />
                                        </div>
                                        <p className="text-sm text-muted-foreground">
                                            Customer: {paymentRequest.customer_name ?? '—'}
                                        </p>
                                        <p className="text-sm text-muted-foreground">
                                            Merchant:{' '}
                                            <span className="font-medium text-foreground">
                                                {paymentRequest.merchant_display_name}
                                            </span>
                                        </p>
                                        <p className="text-sm text-muted-foreground">
                                            Email: {paymentRequest.recipient_email ?? '—'}
                                        </p>
                                        <p className="text-sm text-muted-foreground">
                                            Phone: {paymentRequest.recipient_phone ?? '—'}
                                        </p>
                                        <p className="text-sm text-muted-foreground">
                                            Virtual terminal:{' '}
                                            {paymentRequest.virtual_terminal === undefined
                                                ? 'No VT assigned'
                                                : (getTrimmedNameOrNull(
                                                      paymentRequest.virtual_terminal.name ?? null
                                                  ) ??
                                                  paymentRequest.virtual_terminal
                                                      .processor_device_id)}
                                        </p>
                                    </button>
                                    <div className="space-y-1 text-right text-sm">
                                        <p className="font-semibold">
                                            {formatAmount(
                                                paymentRequest.base_amount ??
                                                    paymentRequest.original_amount,
                                                paymentRequest.currency_code
                                            )}
                                        </p>
                                        <p className="text-muted-foreground">
                                            Date:{' '}
                                            {formatDate(
                                                paymentRequest.created_ts,
                                                paymentRequest.merchant_timezone
                                            )}
                                        </p>
                                        <button
                                            type="button"
                                            aria-label={`Open merchant ${paymentRequest.merchant_display_name}`}
                                            className="text-primary underline-offset-4 hover:underline"
                                            onClick={() => {
                                                breadcrumbNavigate({
                                                    source: 'panel',
                                                    target: {
                                                        url: `/organizations/${paymentRequest.merchant_organization_uuid}`,
                                                        title: 'Organization Details',
                                                    },
                                                });
                                            }}
                                        >
                                            Merchant
                                        </button>
                                    </div>
                                </div>
                            </div>
                        );
                    })}
                </div>
            )}
        />
    );
}
